import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import scrollbar from '../../styles/scrollbar';
import { kindOfKey, renderEntry } from '../../eventLog';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Events log — instrument-cluster read.
 *
 * Mono timestamp · colour tick per kind · serif text. The kind comes from the
 * entry's key — mission = tomato, bid = mustard, win = jade, everything else
 * = walnut chatter. It used to be matched against the rendered sentence,
 * which meant every row of a translated log came out grey.
 */
function stamp(time) {
  const d = new Date(time);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    flex: 1,
    // No `minHeight: 0` here, deliberately. `flex: 1` is `1 1 0%`, and a flex
    // item shrinks in proportion to its base size — so with a zero basis this
    // absorbs none of a sidebar overflow and is handed a height of 0 outright.
    // At `minHeight: 0` it took that literally while `well` still drew its
    // 120px, and the log painted straight over the row below it. The
    // automatic minimum (head + the well's own floor) is what makes the panel
    // scroll instead — which is also why `well` keeps a real 120px floor now
    // that its 260px cap is gone.
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  rule: {
    flex: 1,
    height: '1px',
    background: '#3a2718',
  },
  live: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#2f9e6e',
    boxShadow: '0 0 6px rgba(47,158,110,0.8)',
  },
  well: {
    position: 'relative',
    // The log takes whatever is left between the player list and the lobby's
    // Start Game, rather than stopping at 260px and leaving ~200px of dead
    // panel under it. `minHeight` stays a real 120px and is deliberately not
    // 0: it is the floor that gives `container` — which carries no
    // `minHeight: 0`, see above — an automatic minimum, so a crowded column
    // scrolls the sidebar instead of crushing the log to nothing.
    flex: 1,
    minHeight: '120px',
    background: '#1a0f07',
    borderRadius: '8px',
    boxShadow: 'inset 0 0 0 1px #4a3020, inset 0 2px 6px rgba(0,0,0,0.55)',
    overflow: 'hidden',
  },
  scroll: {
    position: 'absolute',
    inset: 0,
    overflowY: 'auto',
    ...scrollbar,
    padding: '6px 0',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '38px 3px 1fr',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
  },
  rowLatest: {
    background: 'rgba(217,79,43,0.09)',
  },
  time: {
    fontFamily: MONO,
    fontSize: '9.5px',
    letterSpacing: '0.5px',
    color: '#8a6e4c',
  },
  tick: {
    height: '14px',
    borderRadius: '2px',
  },
  text: {
    fontSize: '12.5px',
    lineHeight: 1.25,
  },
  fade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '34px',
    background: 'linear-gradient(0deg, #1a0f07, rgba(26,15,7,0))',
    pointerEvents: 'none',
  },
  empty: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: MONO,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    // The well is now the tallest thing in the column, so its empty state is
    // a large blank field with one line in it — #5c452c was 2.2:1 there and
    // read as nothing at all. #8a6e4c is 3.9:1 on #1a0f07.
    fontSize: '11.5px',
    color: '#8a6e4c',
  },
});

function EventsLog({ log = [] }) {
  const classes = useStyles();
  const t = useT();
  // `t` belongs in the deps: its identity changes with the locale, and that is
  // exactly when every row needs rendering again.
  const rows = useMemo(() => log.map((entry, i) => ({
    key: `${entry.time}-${i}`,
    text: renderEntry(entry, t),
    time: stamp(entry.time),
    latest: i === 0,
    ...kindOfKey(entry.key),
  })), [log, t]);

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <span className={classes.label}>{t('log.title')}</span>
        <span className={classes.rule} />
        <span className={classes.live} />
      </div>
      <div className={classes.well}>
        {rows.length === 0 && <div className={classes.empty}>{t('log.empty')}</div>}
        <div className={classes.scroll}>
          {rows.map(row => (
            <div
              key={row.key}
              className={`${classes.row} ${row.latest ? classes.rowLatest : ''}`}
            >
              <span className={classes.time}>{row.time}</span>
              <span className={classes.tick} style={{ background: row.tick }} />
              <span className={classes.text} style={{ color: row.latest ? '#f0dcc0' : row.color }}>
                {row.text}
              </span>
            </div>
          ))}
        </div>
        {rows.length > 5 && <div className={classes.fade} />}
      </div>
    </div>
  );
}

export default EventsLog;
